/**
 * Render markdown with LaTeX (KaTeX). Used for proactive lesson sections so math
 * displays correctly. Supports \( \), \[ \], $, $$ and :::clarification / :::info / :::warning
 * callouts, :::checklist blocks (optional [title] on the opening line), plus :::mermaid (via learning-content → fences).
 */
import { marked } from "marked";
import DOMPurify from "isomorphic-dompurify";
import katex from "katex";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import dockerfile from "highlight.js/lib/languages/dockerfile";
import json from "highlight.js/lib/languages/json";
import python from "highlight.js/lib/languages/python";
import yaml from "highlight.js/lib/languages/yaml";

hljs.registerLanguage("python", python);
hljs.registerLanguage("py", python);
hljs.registerLanguage("yaml", yaml);
hljs.registerLanguage("yml", yaml);
hljs.registerLanguage("dockerfile", dockerfile);
hljs.registerLanguage("json", json);
hljs.registerLanguage("bash", bash);
hljs.registerLanguage("sh", bash);

const BLOCK_MATH_REGEX = /\$\$([\s\S]*?)\$\$/g;
const INLINE_MATH_REGEX = /\$([^\$\n]+?)\$/g;

function escapeHtml(html: string): string {
  return html
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function renderLatexInHtml(html: string): string {
  let text = html;
  text = text.replace(BLOCK_MATH_REGEX, (_, formula) => {
    try {
      return katex.renderToString(formula.trim(), {
        displayMode: true,
        throwOnError: false,
      });
    } catch {
      return `<span class="text-red-600">${escapeHtml(formula)}</span>`;
    }
  });
  text = text.replace(INLINE_MATH_REGEX, (_, formula) => {
    try {
      return katex.renderToString(formula.trim(), {
        displayMode: false,
        throwOnError: false,
      });
    } catch {
      return `<span class="text-red-600">${escapeHtml(formula)}</span>`;
    }
  });
  return text;
}

/** Normalize \( \) and \[ \] to $ and $$ so we have one code path. Match double-escaped first. */
function normalizeMathDelimiters(text: string): string {
  return text
    .replace(/\\\\\[([\s\S]*?)\\\\\]/g, (_, formula) => "$$" + formula + "$$")
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, formula) => "$$" + formula + "$$")
    .replace(/\\\\\(([\s\S]*?)\\\\\)/g, (_, formula) => "$" + formula + "$")
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, formula) => "$" + formula + "$");
}

/** Add Tailwind-style classes to bare <table> markup from marked. */
function styleTablesInHtml(html: string): string {
  return html
    .replace(/<table>/g, '<div class="my-16 overflow-x-auto border border-[#e5e7eb] bg-white rounded-lg"><table class="w-full border-collapse text-left min-w-[600px]">')
    .replace(/<\/table>/g, '</table></div>')
    .replace(/<thead>/g, '<thead class="bg-[#f8f9fa] border-b border-[#e5e7eb]">')
    .replace(/<th>/g, '<th class="py-2 px-6 border-r border-[#e5e7eb] last:border-r-0 text-[15px] font-semibold text-slate-700 text-left antialiased bg-[#f8f9fa]">')
    .replace(/<th /g, '<th class="py-2 px-6 border-r border-[#e5e7eb] last:border-r-0 text-[15px] font-semibold text-slate-700 text-left antialiased bg-[#f8f9fa]" ')
    .replace(/<tr>/g, '<tr class="border-b border-[#e5e7eb] last:border-0">')
    .replace(/<td>/g, '<td class="py-3 px-6 border-r border-[#e5e7eb] last:border-r-0 text-[15px] text-[#374151] align-top antialiased leading-relaxed">')
    .replace(/<td /g, '<td class="py-3 px-6 border-r border-[#e5e7eb] last:border-r-0 text-[15px] text-[#374151] align-top antialiased leading-relaxed" ');
}

/** Run markdown parse then LaTeX replacement. Does not handle ::: directives. */
function mdToHtmlWithLatex(md: string): string {
  if (!md?.trim()) return "";
  const withMath = normalizeMathDelimiters(md);
  const rawHtml = marked.parse(withMath, { gfm: true, breaks: true }) as string;
  const sanitizedHtml = DOMPurify.sanitize(rawHtml);
  const styledHtml = styleTablesInHtml(sanitizedHtml);
  return renderLatexInHtml(styledHtml);
}

type DirectiveSegment =
  | { type: "clarification"; title: string; content: string }
  | { type: "info"; title: string; content: string }
  | { type: "warning"; title: string; content: string }
  | { type: "steps"; title: string; content: string }
  | { type: "terminal"; title: string; content: string }
  | { type: "code"; title: string; content: string }
  | { type: "output"; title: string; content: string }
  | { type: "platform"; title: string; content: string }
  | { type: "checklist"; title: string; content: string };

type Segment = DirectiveSegment | { type: "md"; content: string };

const DIRECTIVE_BLOCK_RE =
  /(:::(?:clarification|info|warning|steps|terminal|code|output|platform|checklist)(?:[ \t]+[^\n]+)?[ \t]*\n[\s\S]*?\s*:::)/g;

function parseDirectiveBlock(block: string): DirectiveSegment | null {
  const b = block.trim();
  const parseNamed = (name: DirectiveSegment["type"]) => {
    // IMPORTANT: only allow inline args on the opening line.
    // Do not let `\s+` consume a newline, or first body line becomes "title".
    const re = new RegExp(`^:::${name}(?:[ \\t]+([^\\n]+))?[ \\t]*\\n([\\s\\S]*?)\\s*:::\\s*$`);
    const m = b.match(re);
    if (!m) return null;
    return { type: name, title: (m[1] ?? "").trim(), content: m[2].trim() } as DirectiveSegment;
  };
  const names: DirectiveSegment["type"][] = [
    "clarification",
    "info",
    "warning",
    "steps",
    "terminal",
    "code",
    "output",
    "platform",
    "checklist",
  ];
  for (const n of names) {
    const parsed = parseNamed(n);
    if (parsed) return parsed;
  }
  return null;
}

function normalizeDirectiveTitle(raw: string, fallback: string): string {
  const t = raw.trim();
  if (!t) return fallback;
  const unwrapped = t.replace(/^\[(.*)\]$/, "$1").trim();
  return unwrapped || fallback;
}

/** Split content by :::clarification / :::info / :::warning blocks (optional [title] on first line). */
function parseDirectiveContainers(text: string): Segment[] {
  const segments: Segment[] = [];
  let lastEnd = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(DIRECTIVE_BLOCK_RE.source, "g");
  while ((m = re.exec(text)) !== null) {
    const before = text.slice(lastEnd, m.index);
    if (before.trim()) segments.push({ type: "md", content: before });
    const parsed = parseDirectiveBlock(m[0]);
    if (parsed) segments.push(parsed);
    else segments.push({ type: "md", content: m[0] });
    lastEnd = re.lastIndex;
  }
  if (lastEnd < text.length) segments.push({ type: "md", content: text.slice(lastEnd) });
  return segments.length ? segments : [{ type: "md", content: text }];
}

const CALLOUT_INNER_CLASS = "leading-[1.5] [&_p]:mb-0";
const CALLOUT_INNER_STYLE = "font-size: var(--font-size-learning-body)";

function clarificationCalloutHtml(titleLabel: string, innerHtml: string): string {
  return (
    `<div class="my-10 py-4 px-6 bg-sky-50 border-l-4 border-sky-500 rounded-r-xl shadow-sm text-[#111827]">` +
    `<span class="text-sky-800 font-bold uppercase text-[11px] tracking-widest mb-2 block">${titleLabel}</span>` +
    `<div class="${CALLOUT_INNER_CLASS}" style="${CALLOUT_INNER_STYLE}">${innerHtml}</div></div>`
  );
}

function infoCalloutHtml(titleLabel: string, innerHtml: string): string {
  return (
    `<div class="my-10 py-4 px-6 bg-[#f3f4f6] border-l-4 border-[#4f46e5] rounded-r-xl shadow-sm text-[#111827]">` +
    `<span class="text-[#4f46e5] font-bold uppercase text-[11px] tracking-widest mb-2 block">${titleLabel}</span>` +
    `<div class="${CALLOUT_INNER_CLASS}" style="${CALLOUT_INNER_STYLE}">${innerHtml}</div></div>`
  );
}

function warningCalloutHtml(titleLabel: string, innerHtml: string): string {
  return (
    `<div class="my-10 py-4 px-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-xl shadow-sm text-[#111827]">` +
    `<span class="text-amber-700 font-bold uppercase text-[11px] tracking-widest mb-2 block">${titleLabel}</span>` +
    `<div class="${CALLOUT_INNER_CLASS}" style="${CALLOUT_INNER_STYLE}">${innerHtml}</div></div>`
  );
}

function parseFencedCode(content: string): { lang: string; code: string } {
  const m = content.match(/^```([\w-]*)\s*\n([\s\S]*?)\n```$/);
  if (!m) return { lang: "", code: decodeJsonEscapes(content.trim()) };
  return { lang: (m[1] ?? "").trim(), code: decodeJsonEscapes((m[2] ?? "").trimEnd()) };
}

/** Content may arrive with JSON-style escapes (e.g. \`, \", \\n). */
function decodeJsonEscapes(text: string): string {
  return text
    .replace(/\\`/g, "`")
    .replace(/\\"/g, "\"")
    .replace(/\\'/g, "'")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t");
}

function checklistCalloutHtml(titleLabel: string, content: string): string {
  const items = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) =>
      decodeJsonEscapes(line)
        .replace(/^[-*+]\s+\[[xX]\]\s+/, "")
        .replace(/^[-*+]\s+\[[ ]\]\s+/, "")
        .replace(/^\[[xX]\]\s+/, "")
        .replace(/^\[[ ]\]\s+/, "")
        .replace(/^☑\s+/, "")
        .replace(/^☐\s+/, "")
        .trim(),
    )
    .filter(Boolean);
  if (items.length === 0) return "";
  const rows = items
    .map((item) => {
      const inlineHtml = mdToHtmlWithLatex(item).replace(/^<p>/, "").replace(/<\/p>\s*$/, "");
      return (
        `<li class="flex gap-3 rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2.5 text-sm text-slate-800 leading-relaxed">` +
        `<span class="shrink-0 select-none text-base leading-none text-emerald-700" aria-hidden="true">☐</span>` +
        `<span class="min-w-0 flex-1 [&_p]:m-0">${inlineHtml}</span></li>`
      );
    })
    .join("");
  return (
    `<div class="my-8 rounded-xl border border-emerald-200 bg-white p-4 shadow-sm">` +
    `<div class="text-sm font-semibold text-slate-900 mb-3">${titleLabel}</div>` +
    `<ul class="m-0 list-none space-y-2 p-0">${rows}</ul></div>`
  );
}

function stepsCalloutHtml(titleLabel: string, content: string): string {
  const items = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.replace(/^\d+[\).\s-]+/, "").replace(/^[-*+]\s+/, ""))
    .map((line) => decodeJsonEscapes(line))
    .filter(Boolean);
  if (items.length === 0) return "";
  const rows = items
    .map((item, idx) => {
      const inlineHtml = mdToHtmlWithLatex(item).replace(/^<p>/, "").replace(/<\/p>\s*$/, "");
      return `<li class="rounded-lg border border-violet-200 bg-violet-50/40 px-3 py-2 text-sm text-slate-800"><span class="inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-white text-[11px] font-bold mr-2 align-middle">${idx + 1}</span><span class="align-middle">${inlineHtml}</span></li>`;
    })
    .join("");
  return (
    `<div class="my-8 rounded-xl border border-violet-200 bg-white p-4">` +
    `<div class="text-[11px] font-bold uppercase tracking-widest text-violet-700 mb-2">${titleLabel}</div>` +
    `<ol class="space-y-2 m-0 p-0 list-none">${rows}</ol></div>`
  );
}

function terminalCalloutHtml(titleLabel: string, content: string): string {
  const parsed = parseFencedCode(content);
  const lines = parsed.code.split(/\r?\n/);
  const code = lines
    .map((line) => {
      const trimmed = line.trimStart();
      if (!trimmed) return `<div class="h-5"></div>`;
      if (trimmed.startsWith("#")) {
        return `<div><span class="text-slate-400">${escapeHtml(line)}</span></div>`;
      }
      return `<div><span class="text-emerald-300">$</span> <span class="text-slate-100">${escapeHtml(line)}</span></div>`;
    })
    .join("");
  return (
    `<div class="my-8 rounded-xl overflow-hidden border border-slate-700 bg-[#0b1020]">` +
    `<div class="px-4 py-2 border-b border-slate-700 text-[11px] uppercase tracking-widest text-slate-300">${titleLabel}</div>` +
    `<pre class="m-0 p-4 text-[13px] leading-6 overflow-x-auto"><code>${code}</code></pre>` +
    `</div>`
  );
}

function codeCalloutHtml(titleLabel: string, content: string, langHint: string): string {
  const parsed = parseFencedCode(content);
  const lang = (parsed.lang || langHint).trim().toLowerCase();
  const highlighted = lang && hljs.getLanguage(lang)
    ? hljs.highlight(parsed.code, { language: lang, ignoreIllegals: true }).value
    : hljs.highlightAuto(parsed.code).value;
  const languageClass = lang ? `language-${escapeHtml(lang)}` : "language-text";
  const encoded = encodeURIComponent(parsed.code);
  return (
    `<div class="my-8 rounded-xl border border-slate-200 bg-white overflow-hidden">` +
    `<div class="px-4 py-2 border-b border-slate-200 flex items-center justify-between gap-2">` +
    `<div class="flex items-center gap-2">` +
    `<span class="text-[11px] font-bold uppercase tracking-widest text-slate-600">${titleLabel}</span>` +
    `${lang ? `<span class="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-600 uppercase">${escapeHtml(lang)}</span>` : ""}` +
    `</div>` +
    `<button type="button" class="learning-copy-btn text-xs font-semibold px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50" data-copy="${encoded}">Copy</button>` +
    `</div>` +
    `<pre class="m-0 p-4 text-[13px] leading-6 overflow-x-auto bg-slate-50"><code class="hljs ${languageClass}">${highlighted}</code></pre>` +
    `</div>`
  );
}

function outputCalloutHtml(titleLabel: string, content: string): string {
  const clean = decodeJsonEscapes(content.trim());
  return (
    `<div class="my-8 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">` +
    `<div class="text-[11px] font-bold uppercase tracking-widest text-emerald-700 mb-2">${titleLabel}</div>` +
    `<pre class="m-0 text-[13px] leading-6 text-emerald-900 whitespace-pre-wrap">${escapeHtml(clean)}</pre>` +
    `</div>`
  );
}

function platformCalloutHtml(titleLabel: string, content: string): string {
  const entries = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const badges = entries
    .map((entry) => {
      const [name, ...descParts] = entry.split(":");
      const desc = descParts.join(":").trim();
      return `<span class="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-2.5 py-1 text-xs text-slate-700 bg-slate-50"><span class="font-semibold">${escapeHtml(name.trim())}</span>${desc ? `<span class="text-slate-500">- ${escapeHtml(desc)}</span>` : ""}</span>`;
    })
    .join(" ");
  return (
    `<div class="my-6 rounded-lg border border-slate-200 bg-slate-50 p-3">` +
    `<div class="text-[11px] font-bold uppercase tracking-widest text-slate-600 mb-2">${titleLabel}</div>` +
    `<div class="flex flex-wrap gap-2">${badges}</div></div>`
  );
}

/**
 * Convert markdown string to HTML with LaTeX (KaTeX) and directive callouts.
 */
export function markdownWithLatexToHtml(md: string): string {
  if (!md?.trim()) return "";
  const segments = parseDirectiveContainers(md);
  const parts: string[] = [];
  for (const seg of segments) {
    if (seg.type === "clarification") {
      const innerHtml = mdToHtmlWithLatex(seg.content);
      const titleLabel = escapeHtml(normalizeDirectiveTitle(seg.title, "Clarification"));
      parts.push(clarificationCalloutHtml(titleLabel, innerHtml));
    } else if (seg.type === "info") {
      const innerHtml = mdToHtmlWithLatex(seg.content);
      const titleLabel = escapeHtml(normalizeDirectiveTitle(seg.title, "Information"));
      parts.push(infoCalloutHtml(titleLabel, innerHtml));
    } else if (seg.type === "warning") {
      const innerHtml = mdToHtmlWithLatex(seg.content);
      const titleLabel = escapeHtml(normalizeDirectiveTitle(seg.title, "Warning"));
      parts.push(warningCalloutHtml(titleLabel, innerHtml));
    } else if (seg.type === "steps") {
      const titleLabel = escapeHtml(normalizeDirectiveTitle(seg.title, "Steps"));
      parts.push(stepsCalloutHtml(titleLabel, seg.content));
    } else if (seg.type === "terminal") {
      const titleLabel = escapeHtml(normalizeDirectiveTitle(seg.title, "Terminal"));
      parts.push(terminalCalloutHtml(titleLabel, seg.content));
    } else if (seg.type === "code") {
      const titleLabel = escapeHtml("Code");
      const langHint = normalizeDirectiveTitle(seg.title, "");
      parts.push(codeCalloutHtml(titleLabel, seg.content, langHint));
    } else if (seg.type === "output") {
      const titleLabel = escapeHtml(normalizeDirectiveTitle(seg.title, "Expected Output"));
      parts.push(outputCalloutHtml(titleLabel, seg.content));
    } else if (seg.type === "platform") {
      const titleLabel = escapeHtml(normalizeDirectiveTitle(seg.title, "Platform"));
      parts.push(platformCalloutHtml(titleLabel, seg.content));
    } else if (seg.type === "checklist") {
      const titleLabel = escapeHtml(normalizeDirectiveTitle(seg.title, "Checklist"));
      parts.push(checklistCalloutHtml(titleLabel, seg.content));
    } else {
      parts.push(mdToHtmlWithLatex(seg.content));
    }
  }
  return parts.join("\n");
}
